import React, { useState } from 'react';
import { Menu, X, ChevronDown, Radio, Share2, FileText, Rocket, Bot, Send, Bell, Crown, Coins, Puzzle, Mic, Settings, Building2, Shield, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [appsDropdownOpen, setAppsDropdownOpen] = useState(false);
  const [botsDropdownOpen, setBotsDropdownOpen] = useState(false);
  const [developersDropdownOpen, setDevelopersDropdownOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  // Mobile menu accordion states
  const [mobileAppsOpen, setMobileAppsOpen] = useState(false);
  const [mobileDevelopersOpen, setMobileDevelopersOpen] = useState(false);
  const [mobileBotsOpen, setMobileBotsOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  const apps = [
    {
      name: 'QuickSync Me',
      description: 'Fastest and simplest way to share and collaborate privately.',
      icon: '/quicksync-me-icon.png',
      href: 'https://quicksync.me/',
      isNew: false
    },
    {
      name: 'StageX',
      description: 'Live streams where communities ask, vote, buy and act on-chain.',
      icon: 'https://customer-assets.emergentagent.com/job_9c600170-5d16-447d-bfe0-1d0de6d7d09f/artifacts/b6z8aocx_StageX.png',
      href: 'https://stagex.space/',
      isNew: true
    }
  ];

  const developerItems = [
    {
      name: 'APIs',
      description: 'RESTful endpoints to integrate QS into your apps.',
      iconType: 'api',
      href: 'https://docs.quicksync.me/qs-framework',
      isNew: false,
      isComingSoon: false
    },
    {
      name: 'Developer Docs',
      description: 'Guides, references, and tutorials to get started.',
      iconType: 'docs',
      href: 'https://docs.quicksync.me/qs-framework',
      isNew: false,
      isComingSoon: false
    },
    {
      name: 'Get Incubated',
      description: 'We fund and support projects building on QS.',
      iconType: 'rocket',
      href: 'mailto:info@quicksync.me',
      isNew: false,
      isComingSoon: false
    },
    {
      name: 'SDKs',
      description: 'Libraries and tools for seamless development.',
      iconType: 'sdk',
      href: null,
      isNew: false,
      isComingSoon: true
    }
  ];

  const botItems = [
    {
      name: 'QuickSync Me Bot',
      description: 'Share data securely directly in Telegram.',
      iconType: 'bot1',
      href: 'https://t.me/qs_share_bot',
      isNew: false,
      isComingSoon: false
    },
    {
      name: 'StageX Butler',
      description: 'Your assistant for StageX reminders and alerts.',
      iconType: 'bot2',
      href: 'https://t.me/StageXButlerBot',
      isNew: false,
      isComingSoon: false
    }
  ];

  const serviceItems = [
    {
      name: 'Premium Subscriptions',
      description: 'Unlock advanced features across QS Apps.',
      iconType: 'crown',
      href: 'mailto:info@quicksync.me',
      actionButton: null,
      isComingSoon: true
    },
    {
      name: 'Quicks',
      description: 'Your credit gateway for InteractionFi.',
      iconType: 'coins',
      href: 'mailto:info@quicksync.me',
      actionButton: null,
      isComingSoon: true
    },
    {
      name: 'QS Framework Access',
      description: 'Build privacy-first workspaces.',
      iconType: 'framework',
      href: 'mailto:info@quicksync.me',
      actionButton: null
    },
    {
      name: 'Integrations',
      description: 'Seamlessly embed QS Apps into your existing applications.',
      iconType: 'puzzle',
      href: 'mailto:info@quicksync.me',
      actionButton: null
    },
    {
      name: 'AMAs',
      description: 'Interactive AMAs with top hosts on StageX.',
      iconType: 'mic',
      href: 'mailto:info@quicksync.me',
      actionButton: null
    },
    {
      name: 'Customized Solutions',
      description: 'Get bespoke dApps and tools built on QS infrastructure.',
      iconType: 'settings',
      href: 'mailto:info@quicksync.me',
      actionButton: null
    }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src="/quicksync-logo.png" 
              alt="QuickSync" 
              className="h-8 w-auto"
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {/* Apps Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setAppsDropdownOpen(true)}
              onMouseLeave={() => setAppsDropdownOpen(false)}
            >
              <div className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-sm font-medium py-2">
                <span>Apps</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${appsDropdownOpen ? 'rotate-180' : ''}`} />
              </div>
              
              {/* Professional Dropdown Menu */}
              {appsDropdownOpen && (
                <>
                  {/* Invisible bridge to prevent gap issues */}
                  <div className="absolute top-full left-0 right-0 h-4"></div>
                  
                  <div className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 w-[920px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                    {/* Arrow indicator */}
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45"></div>
                    
                    {/* Apps List - Horizontal, Proton Style */}
                    <div className="p-4">
                      <div className="grid grid-cols-3 gap-2">
                        {apps.map((app, index) => (
                          <a
                            key={index}
                            href={app.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex flex-col p-4 rounded-xl hover:bg-gray-50 transition-all duration-200 group"
                          >
                            {/* Top row: Icon + Name */}
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 shadow-sm flex items-center justify-center group-hover:shadow-md transition-shadow flex-shrink-0">
                                <img 
                                  src={app.icon} 
                                  alt={app.name}
                                  className="w-6 h-6 object-contain"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <h4 className="text-base font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                                  {app.name}
                                </h4>
                                {app.isNew && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                    New
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* Description */}
                            <p className="text-sm text-gray-500 leading-relaxed text-left">
                              {app.description}
                            </p>
                          </a>
                        ))}
                        
                        {/* Coming Soon Teaser */}
                        <div className="flex flex-col p-4 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 transition-all duration-200 group hover:border-gray-300 hover:bg-gray-50">
                          {/* Top row: Icon + Name */}
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            </div>
                            <div className="flex items-center gap-2">
                              <h4 className="text-base font-semibold text-gray-400">
                                More Apps
                              </h4>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                Soon
                              </span>
                            </div>
                          </div>
                          {/* Description */}
                          <p className="text-sm text-gray-400 leading-relaxed text-left">
                            New experiences are on the way. Stay tuned!
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Footer - QS Framework */}
                    <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-5">
                      <a
                        href="https://docs.quicksync.me/qs-framework"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 group"
                      >
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_9c600170-5d16-447d-bfe0-1d0de6d7d09f/artifacts/wg56q37t_Framework%20QS.png"
                            alt="QS Framework"
                            className="w-7 h-7 object-contain"
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-base font-semibold text-white group-hover:text-emerald-400 transition-colors">
                            QS Framework
                          </h4>
                          <p className="text-sm text-gray-400">
                            Build your own private apps
                          </p>
                        </div>
                        <div className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium py-2 px-4 rounded-lg transition-all duration-200">
                          <span>Get Started</span>
                          <ChevronDown className="w-4 h-4 -rotate-90" />
                        </div>
                      </a>
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Developers Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setDevelopersDropdownOpen(true)}
              onMouseLeave={() => setDevelopersDropdownOpen(false)}
            >
              <div className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-sm font-medium py-2">
                <span>Developers</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${developersDropdownOpen ? 'rotate-180' : ''}`} />
              </div>
              
              {/* Developers Dropdown Menu */}
              {developersDropdownOpen && (
                <>
                  {/* Invisible bridge to prevent gap issues */}
                  <div className="absolute top-full left-0 right-0 h-4"></div>
                  
                  <div className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 w-[920px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                    {/* Arrow indicator */}
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45"></div>
                    
                    {/* Developer Items - Horizontal Grid */}
                    <div className="p-4">
                      <div className="grid grid-cols-4 gap-2">
                        {developerItems.map((item, index) => {
                          const ItemContent = () => (
                            <>
                              {/* Top row: Icon + Name */}
                              <div className="flex items-center gap-3 mb-2">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-shadow ${
                                  item.isComingSoon 
                                    ? 'bg-gray-100 border border-gray-200' 
                                    : 'bg-white border border-gray-100 shadow-sm group-hover:shadow-md'
                                }`}>
                                  {item.iconType === 'api' ? (
                                    <svg className={`w-5 h-5 ${item.isComingSoon ? 'text-gray-400' : 'text-emerald-600'}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                    </svg>
                                  ) : item.iconType === 'docs' ? (
                                    <FileText className={`w-5 h-5 ${item.isComingSoon ? 'text-gray-400' : 'text-emerald-600'}`} />
                                  ) : item.iconType === 'rocket' ? (
                                    <Rocket className={`w-5 h-5 ${item.isComingSoon ? 'text-gray-400' : 'text-emerald-600'}`} />
                                  ) : item.iconType === 'sdk' ? (
                                    <svg className={`w-5 h-5 ${item.isComingSoon ? 'text-gray-400' : 'text-emerald-600'}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                  ) : null}
                                </div>
                                <div className="flex items-center gap-2">
                                  <h4 className={`text-base font-semibold transition-colors ${
                                    item.isComingSoon 
                                      ? 'text-gray-400' 
                                      : 'text-gray-900 group-hover:text-emerald-600'
                                  }`}>
                                    {item.name}
                                  </h4>
                                  {item.isComingSoon && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                      Soon
                                    </span>
                                  )}
                                </div>
                              </div>
                              {/* Description */}
                              <p className={`text-sm leading-relaxed text-left ${
                                item.isComingSoon ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                {item.description}
                              </p>
                            </>
                          );

                          if (item.href) {
                            return (
                              <a
                                key={index}
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex flex-col p-4 rounded-xl transition-all duration-200 group ${
                                  item.isComingSoon 
                                    ? 'border-2 border-dashed border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-50' 
                                    : 'hover:bg-gray-50'
                                }`}
                              >
                                <ItemContent />
                              </a>
                            );
                          }

                          return (
                            <div
                              key={index}
                              className={`flex flex-col p-4 rounded-xl transition-all duration-200 group cursor-default ${
                                item.isComingSoon 
                                  ? 'border-2 border-dashed border-gray-200 bg-gray-50/50' 
                                  : 'bg-gray-50'
                              }`}
                            >
                              <ItemContent />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Bots Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setBotsDropdownOpen(true)}
              onMouseLeave={() => setBotsDropdownOpen(false)}
            >
              <div className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-sm font-medium py-2">
                <span>Bots</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${botsDropdownOpen ? 'rotate-180' : ''}`} />
              </div>
              
              {/* Bots Dropdown Menu */}
              {botsDropdownOpen && (
                <>
                  {/* Invisible bridge to prevent gap issues */}
                  <div className="absolute top-full left-0 right-0 h-4"></div>
                  
                  <div className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 w-[520px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                    {/* Arrow indicator */}
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45"></div>
                    
                    {/* Bot Items - Horizontal Grid */}
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-2">
                        {botItems.map((item, index) => (
                          <a
                            key={index}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex flex-col p-4 rounded-xl hover:bg-gray-50 transition-all duration-200 group"
                          >
                            {/* Top row: Icon + Name */}
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 shadow-sm flex items-center justify-center group-hover:shadow-md transition-shadow flex-shrink-0">
                                {item.iconType === 'bot1' ? (
                                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                  </svg>
                                ) : item.iconType === 'bot2' ? (
                                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                ) : null}
                              </div>
                              <div className="flex items-center gap-2">
                                <h4 className="text-base font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                                  {item.name}
                                </h4>
                                {item.isNew && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                    New
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* Description */}
                            <p className="text-sm text-gray-500 leading-relaxed text-left">
                              {item.description}
                            </p>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setServicesDropdownOpen(true)}
              onMouseLeave={() => setServicesDropdownOpen(false)}
            >
              <div className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-sm font-medium py-2">
                <span>Services</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${servicesDropdownOpen ? 'rotate-180' : ''}`} />
              </div>
              
              {/* Services Dropdown Menu */}
              {servicesDropdownOpen && (
                <>
                  {/* Invisible bridge to prevent gap issues */}
                  <div className="absolute top-full left-0 right-0 h-4"></div>
                  
                  <div className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 w-[980px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                    {/* Arrow indicator */}
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-gray-100 rotate-45"></div>
                    
                    <div className="flex">
                      {/* Left Side - Services Grid (3 columns) */}
                      <div className="flex-1 p-5">
                        <div className="grid grid-cols-3 gap-3">
                          {serviceItems.map((item, index) => (
                            <a
                              key={index}
                              href={item.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex flex-col p-4 rounded-xl hover:bg-gray-50 transition-all duration-200 group"
                            >
                              {/* Top row: Icon + Name */}
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-9 h-9 rounded-lg bg-white border border-gray-100 shadow-sm flex items-center justify-center group-hover:shadow-md transition-shadow flex-shrink-0">
                                  {item.iconType === 'crown' ? (
                                    <Crown className="w-4 h-4 text-emerald-600" />
                                  ) : item.iconType === 'coins' ? (
                                    <Coins className="w-4 h-4 text-emerald-600" />
                                  ) : item.iconType === 'framework' ? (
                                    <img 
                                      src="https://customer-assets.emergentagent.com/job_9c600170-5d16-447d-bfe0-1d0de6d7d09f/artifacts/wg56q37t_Framework%20QS.png"
                                      alt="QS Framework"
                                      className="w-5 h-5 object-contain"
                                    />
                                  ) : item.iconType === 'puzzle' ? (
                                    <Puzzle className="w-4 h-4 text-emerald-600" />
                                  ) : item.iconType === 'mic' ? (
                                    <Mic className="w-4 h-4 text-emerald-600" />
                                  ) : item.iconType === 'settings' ? (
                                    <Settings className="w-4 h-4 text-emerald-600" />
                                  ) : null}
                                </div>
                                <h4 className="text-sm font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                                  {item.name}
                                </h4>
                              </div>
                              {/* Description */}
                              <p className="text-xs text-gray-500 leading-relaxed text-left mb-2">
                                {item.description}
                              </p>
                              {/* Action Button or Coming Soon Tag */}
                              {item.actionButton && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 mt-auto">
                                  {item.actionButton.text}
                                  <ArrowRight className="w-3 h-3" />
                                </span>
                              )}
                              {item.isComingSoon && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 mt-auto">
                                  Coming Soon
                                </span>
                              )}
                            </a>
                          ))}
                        </div>
                      </div>
                      
                      {/* Right Side - QS for Enterprise */}
                      <div className="w-[280px] bg-gradient-to-br from-gray-900 to-gray-800 p-6 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Building2 className="w-5 h-5 text-emerald-400" />
                            <h4 className="text-white font-bold text-lg">QS for Enterprise</h4>
                          </div>
                          <p className="text-gray-300 text-sm leading-relaxed mb-4">
                            Protect your business with QS on-premise. Full control, maximum security, tailored for your organization.
                          </p>
                          <div className="flex items-center gap-2 mb-4">
                            <Shield className="w-4 h-4 text-emerald-400" />
                            <span className="text-gray-400 text-xs">Enterprise-grade security</span>
                          </div>
                        </div>
                        <a 
                          href="mailto:info@quicksync.me"
                          className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-all duration-200"
                        >
                          Contact Sales
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* News Link */}
            <a href="https://medium.com/@quicksync" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium">
              News
            </a>
          </div>

          {/* Right Side */}
          <div className="hidden md:flex items-center gap-4">
            <a 
              href="https://app.uniswap.org/explore/tokens/ethereum/0xEbFA41da8978Bb4554eF119c403E6cDB2E63AeD3"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 transition-all duration-300 hover:scale-105">
                Buy $QS
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed left-0 right-0 top-[72px] bottom-0 bg-white z-[100] overflow-y-auto border-t border-gray-200" style={{height: 'calc(100vh - 72px)'}}>
            <div className="px-6 py-4 space-y-2 min-h-full">
              {/* Apps Section */}
              <div className="border-b border-gray-100 pb-2">
                <button 
                  onClick={() => setMobileAppsOpen(!mobileAppsOpen)}
                  className="flex items-center justify-between w-full py-3 text-gray-900 font-medium"
                >
                  <span>Apps</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${mobileAppsOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileAppsOpen && (
                  <div className="pl-4 pb-2 space-y-1">
                    {apps.map((app, index) => (
                      <a 
                        key={index}
                        href={app.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-gray-500 hover:text-gray-900 transition-colors py-2"
                      >
                        <img src={app.icon} alt={app.name} className="w-6 h-6 object-contain" />
                        <span>{app.name}</span>
                        {app.isNew && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">New</span>
                        )}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Developers Section */}
              <div className="border-b border-gray-100 pb-2">
                <button 
                  onClick={() => setMobileDevelopersOpen(!mobileDevelopersOpen)}
                  className="flex items-center justify-between w-full py-3 text-gray-900 font-medium"
                >
                  <span>Developers</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${mobileDevelopersOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileDevelopersOpen && (
                  <div className="pl-4 pb-2 space-y-1">
                    {developerItems.map((item, index) => {
                      const itemContent = (
                        <>
                          {item.iconType === 'api' ? (
                            <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                            </svg>
                          ) : item.iconType === 'docs' ? (
                            <FileText className="w-5 h-5 text-emerald-600" />
                          ) : item.iconType === 'rocket' ? (
                            <Rocket className="w-5 h-5 text-emerald-600" />
                          ) : item.iconType === 'sdk' ? (
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                          ) : null}
                          <span>{item.name}</span>
                          {item.isComingSoon && (
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Soon</span>
                          )}
                        </>
                      );

                      if (item.href) {
                        return (
                          <a 
                            key={index}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 text-gray-500 hover:text-gray-900 transition-colors py-2"
                          >
                            {itemContent}
                          </a>
                        );
                      }

                      return (
                        <div 
                          key={index}
                          className="flex items-center gap-3 text-gray-400 py-2 cursor-default"
                        >
                          {itemContent}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Bots Section */}
              <div className="border-b border-gray-100 pb-2">
                <button 
                  onClick={() => setMobileBotsOpen(!mobileBotsOpen)}
                  className="flex items-center justify-between w-full py-3 text-gray-900 font-medium"
                >
                  <span>Bots</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${mobileBotsOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileBotsOpen && (
                  <div className="pl-4 pb-2 space-y-1">
                    {botItems.map((item, index) => (
                      <a 
                        key={index}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-gray-500 hover:text-gray-900 transition-colors py-2"
                      >
                        {item.iconType === 'bot1' ? (
                          <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                          </svg>
                        ) : item.iconType === 'bot2' ? (
                          <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        ) : null}
                        <span>{item.name}</span>
                        {item.isNew && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">New</span>
                        )}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Services Section */}
              <div className="border-b border-gray-100 pb-2">
                <button 
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="flex items-center justify-between w-full py-3 text-gray-900 font-medium"
                >
                  <span>Services</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileServicesOpen && (
                  <div className="pl-4 pb-2 space-y-1">
                    {serviceItems.map((item, index) => (
                      <a 
                        key={index}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-gray-500 hover:text-gray-900 transition-colors py-2"
                      >
                        {item.iconType === 'crown' ? (
                          <Crown className="w-5 h-5 text-emerald-600" />
                        ) : item.iconType === 'coins' ? (
                          <Coins className="w-5 h-5 text-emerald-600" />
                        ) : item.iconType === 'framework' ? (
                          <img 
                            src="https://customer-assets.emergentagent.com/job_9c600170-5d16-447d-bfe0-1d0de6d7d09f/artifacts/wg56q37t_Framework%20QS.png"
                            alt="QS Framework"
                            className="w-5 h-5 object-contain"
                          />
                        ) : item.iconType === 'puzzle' ? (
                          <Puzzle className="w-5 h-5 text-emerald-600" />
                        ) : item.iconType === 'mic' ? (
                          <Mic className="w-5 h-5 text-emerald-600" />
                        ) : item.iconType === 'settings' ? (
                          <Settings className="w-5 h-5 text-emerald-600" />
                        ) : null}
                        <span>{item.name}</span>
                        {item.isComingSoon && (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Soon</span>
                        )}
                      </a>
                    ))}
                    <a 
                      href="mailto:info@quicksync.me" 
                      className="flex items-center gap-3 text-gray-500 hover:text-gray-900 transition-colors py-2"
                    >
                      <Building2 className="w-5 h-5 text-emerald-600" />
                      <span>QS for Enterprise</span>
                    </a>
                  </div>
                )}
              </div>

              {/* News Link */}
              <a 
                href="https://medium.com/@quicksync" 
                target="_blank"
                rel="noopener noreferrer"
                className="block py-3 text-gray-900 font-medium border-b border-gray-100"
              >
                News
              </a>

              {/* Buy $QS Button */}
              <div className="pt-4 pb-8">
                <a 
                  href="https://app.uniswap.org/explore/tokens/ethereum/0xEbFA41da8978Bb4554eF119c403E6cDB2E63AeD3"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-3">
                    Buy $QS
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;